import { cx } from "@emotion/css";
import CircularProgress from "@mui/material/CircularProgress";
import MuiFormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MuiTextField, { type TextFieldProps } from "@mui/material/TextField";
import { CircleAlert, X } from "lucide-react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/components/Default/LekUI/Button";

type ExtendedTextFieldProps = TextFieldProps & {
	loading?: boolean;
	onClear?: () => void;
};

// slotProps od wywołującego + nasze dodatki do slotu input (MUI 9: zamiast InputProps)
const withInputSlot = (
	slotProps: TextFieldProps["slotProps"],
	extra: Record<string, unknown>,
): TextFieldProps["slotProps"] => {
	const input = slotProps?.input;
	return {
		...slotProps,
		input: { ...(typeof input === "object" ? input : {}), ...extra },
	};
};

const getEndAdornment = (
	loading?: boolean,
	hasValue?: boolean,
	onClear?: () => void,
) => {
	if (loading) {
		return (
			<InputAdornment position="end">
				<CircularProgress size={18} />
			</InputAdornment>
		);
	}
	if (hasValue && onClear) {
		return (
			<InputAdornment position="end">
				<IconButton onClick={onClear} size="small">
					<X className="icon-small icon-black" />
				</IconButton>
			</InputAdornment>
		);
	}
	return undefined;
};

export const TextFieldGrey = forwardRef<HTMLDivElement, ExtendedTextFieldProps>(
	function TextFieldGrey(props, ref) {
		const { className, loading, onClear, slotProps, ...otherProps } = props;
		const isFilled = !props.variant || props.variant === "filled";
		const endAdornment = getEndAdornment(loading, !!props.value, onClear);

		return (
			<MuiTextField
				{...otherProps}
				ref={ref}
				className={cx(className, "text-field--grey")}
				slotProps={withInputSlot(slotProps, {
					...(isFilled && { disableUnderline: true }),
					...(endAdornment && { endAdornment }),
				})}
			/>
		);
	},
);

interface CustomTextFieldProps extends Omit<ExtendedTextFieldProps, "error"> {
	error?: string | boolean;
}

export const TextField = forwardRef<HTMLDivElement, CustomTextFieldProps>(
	function TextField(props, ref) {
		const {
			className,
			error,
			label,
			loading,
			onClear,
			slotProps,
			...otherProps
		} = props;
		const isFilled = !props.variant || props.variant === "filled";
		const builtInEndAdornment = getEndAdornment(
			loading,
			!!props.value,
			onClear,
		);
		return (
			<MuiTextField
				{...otherProps}
				error={!!error}
				label={error || label}
				ref={ref}
				className={cx(className, "text-field")}
				slotProps={withInputSlot(slotProps, {
					...(isFilled && { disableUnderline: true }),
					...(builtInEndAdornment && { endAdornment: builtInEndAdornment }),
				})}
			/>
		);
	},
);

interface TextFieldWithSaveProps
	extends Omit<CustomTextFieldProps, "onSubmit"> {
	onSave: () => void | Promise<void>;
	onCancel: () => void;
	isSaving?: boolean;
	saveDisabled?: boolean;
	primaryButtonClassName?: string;
}

export const TextFieldWithSave = forwardRef<
	HTMLDivElement,
	TextFieldWithSaveProps
>(function TextFieldWithSave(props, ref) {
	const { onSave, onCancel, isSaving, saveDisabled, onKeyDown, ...rest } =
		props;
	const { t } = useTranslation();

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" && !isSaving && !saveDisabled) {
			event.preventDefault();
			onSave();
		} else if (event.key === "Escape") {
			event.preventDefault();
			onCancel();
		}
		onKeyDown?.(event);
	};

	return (
		<TextField
			{...rest}
			ref={ref}
			onKeyDown={handleKeyDown}
			slotProps={withInputSlot(rest.slotProps, {
				endAdornment: (
					<InputAdornment position="end" className="flex items-center gap-8">
						<button
							type="button"
							className="link-button"
							onClick={onCancel}
							disabled={isSaving}
							data-test-id="text-field-cancel-button"
						>
							{t("commands.cancel")}
						</button>
						<PrimaryButton
							onClick={onSave}
							disabled={isSaving || saveDisabled}
							data-test-id="text-field-save-button"
							className={props.primaryButtonClassName}
						>
							{t("commands.save")}
						</PrimaryButton>
					</InputAdornment>
				),
			})}
		/>
	);
});

interface FormTextFieldProps
	extends Omit<TextFieldProps, "error" | "helperText"> {
	/** Komunikat błędu; renderowany pod polem z ikoną (nowy wzorzec walidacji). */
	error?: string | false;
}

export const FormTextField = forwardRef<HTMLDivElement, FormTextFieldProps>(
	function FormTextField({ className, error, ...rest }, ref) {
		return (
			<MuiTextField
				{...rest}
				ref={ref}
				variant="outlined"
				error={!!error}
				className={cx(className, "text-field", "text-field--form")}
				helperText={
					error && (
						<span className="flex items-center gap-8">
							<CircleAlert className="icon-super-small flex-shrink-0" />
							{error}
						</span>
					)
				}
			/>
		);
	},
);

export const FormControl = MuiFormControl;
